import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { fetchAllSnapshots } from "@/lib/api";
import RegionSelect from "@/components/RegionSelect";
import { X } from "@phosphor-icons/react";

const COLORS = ["#00FA9A", "#FFC24B", "#4DA3FF", "#FF7A45"];

export default function Compare() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    (async () => {
      const d = await fetchAllSnapshots();
      setData(d);
      if (d?.regions?.length) setSelected([d.regions[0].id]);
    })();
  }, []);

  const regions = useMemo(() => data?.regions || [], [data]);
  const snapMap = useMemo(() => {
    const m = {};
    (data?.snapshots || []).forEach((s) => { m[s.region_id] = s; });
    return m;
  }, [data]);

  const chartData = useMemo(() => {
    if (!selected.length) return [];
    const series = selected.map((id) => ({
      id,
      label: regions.find((r) => r.id === id)?.label || id,
      ndvi: snapMap[id]?.satellite?.ndvi || [],
      ndmi: snapMap[id]?.satellite?.ndmi || [],
    }));
    const n = Math.max(...series.map((s) => s.ndvi.length), 0);
    return Array.from({ length: n }, (_, i) => {
      const row = { day: `T-${n - i - 1}` };
      series.forEach((s) => { row[s.label] = s.ndvi[i]; });
      return row;
    });
  }, [selected, regions, snapMap]);

  const addRegion = (rid) => {
    if (selected.includes(rid) || selected.length >= 4) return;
    setSelected([...selected, rid]);
  };
  const removeRegion = (rid) => setSelected(selected.filter((x) => x !== rid));

  return (
    <div className="space-y-5" data-testid="compare-root">
      <header>
        <div className="eid-label">Vergleich · Regionen</div>
        <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">Regionen-Vergleich</h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Wähle bis zu 4 Regionen und sieh, wie sie sich in den letzten 30 Tagen entwickelt haben.
          Ideal für „meine Region vs. Nachbar-Bundesland".
        </p>
      </header>

      <div className="eid-card p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="eid-label">Hinzufügen:</span>
          <RegionSelect
            regions={regions.filter((r) => !selected.includes(r.id))}
            value=""
            onChange={addRegion}
            testid="compare-add"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {selected.map((id, i) => {
            const r = regions.find((x) => x.id === id);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs"
                style={{ background: COLORS[i] + "22", color: COLORS[i] }}
                data-testid={`compare-chip-${id}`}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i] }} />
                {r?.label || id}
                <button onClick={() => removeRegion(id)} className="hover:opacity-60"><X size={12} /></button>
              </span>
            );
          })}
        </div>
      </div>

      {chartData.length > 0 && (
        <div className="eid-card p-4" style={{ height: 380 }}>
          <div className="eid-label mb-2">NDVI · 30 Tage</div>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="day" fontSize={10} />
              <YAxis domain={[0, 1]} fontSize={10} />
              <Tooltip />
              <Legend />
              {selected.map((id, i) => {
                const label = regions.find((r) => r.id === id)?.label || id;
                return <Line key={id} type="monotone" dataKey={label} stroke={COLORS[i]} strokeWidth={2} dot={false} />;
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Metrics comparison */}
      {selected.length > 0 && (
        <div className="eid-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A]">
              <tr className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                <th className="text-left p-3">Kennzahl</th>
                {selected.map((id, i) => (
                  <th key={id} className="text-right p-3" style={{ color: COLORS[i] }}>
                    {regions.find((r) => r.id === id)?.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METRIC_ROWS.map((row) => (
                <tr key={row.key} className="border-t border-white/10">
                  <td className="p-3 font-medium">{row.label}</td>
                  {selected.map((id) => {
                    const v = snapMap[id]?.metrics?.[row.key];
                    return (
                      <td key={id} className="p-3 text-right font-mono text-xs">
                        {v != null ? (row.fmt ? row.fmt(v) : v.toFixed?.(2)) : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const METRIC_ROWS = [
  { key: "ndvi_current", label: "NDVI aktuell" },
  { key: "ndvi_anomaly", label: "NDVI-Abweichung vs. 30-Tage-Schnitt", fmt: (v) => (v >= 0 ? "+" : "") + v.toFixed(3) },
  { key: "soil_moisture_current", label: "Bodenfeuchte m³/m³" },
  { key: "soil_moisture_deficit", label: "Bodenfeuchte-Defizit" },
  { key: "yield_forecast_index", label: "Ertragsprognose /100", fmt: (v) => v.toFixed(0) },
  { key: "forest_vitality_index", label: "Forst-Vitalität /100", fmt: (v) => v.toFixed(0) },
  { key: "price_change_7d_pct", label: "Preis-Δ 7 Tage %", fmt: (v) => (v >= 0 ? "+" : "") + v.toFixed(2) + "%" },
];
